/**
 * MemberOutreachHistoryTab — read-only outreach traceability on the member record.
 *
 * Surfaces the full outreach trail for one member (previously only reachable
 * inside the outreach console): every billable touch, every saved SOAP
 * progress note (all versions), and every generated billing claim. Data comes
 * from GET /api/outreach-billing/{member_id}/history, scoped to the member's
 * outreach enrollment, so nothing crosses members.
 */
import React, { useEffect, useState } from 'react';
import { Phone, MessageSquare, Mail, FileText, Receipt, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import outreachBillingApi, { OutreachMemberHistory } from '@/services/api/outreachBilling';
import { formatLocalDateTime } from '@/utils/dateTime';

const SOAP_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'subjective', label: 'Subjective' },
  { key: 'objective', label: 'Objective' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'plan', label: 'Plan' },
];

const channelIcon = (c: string) =>
  c === 'phone' ? <Phone className="w-3.5 h-3.5" /> : c === 'email' ? <Mail className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />;

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  try {
    // Backend timestamps are UTC (often naive); render in the viewer's system tz.
    return formatLocalDateTime(d, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return d;
  }
};

interface Props {
  memberId: number;
}

export default function MemberOutreachHistoryTab({ memberId }: Props) {
  const [data, setData] = useState<OutreachMemberHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    outreachBillingApi
      .getMemberHistory(memberId)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e: any) => {
        if (alive) setError(e?.response?.data?.detail || e?.message || 'Failed to load outreach history');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [memberId]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading outreach history…
      </div>
    );
  }
  if (error) {
    return <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">{error}</div>;
  }
  if (!data) return null;

  const empty = data.touches.length === 0 && data.notes.length === 0 && data.claims.length === 0;
  if (empty) {
    return (
      <div className="p-8 text-center text-sm text-gray-500">
        No outreach activity recorded for this member yet.
        <div className="text-xs text-gray-400 mt-1">
          Touches, progress notes, and billing claims logged in the outreach console will appear here.
        </div>
      </div>
    );
  }

  const t = data.totals;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Touches', value: t.touch_count },
          { label: 'Progress notes', value: t.note_count },
          { label: 'Claims', value: t.claim_count },
          { label: 'Billed', value: `$${t.billed_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border bg-white dark:bg-gray-800 px-3 py-2">
            <div className="text-xs text-gray-500">{s.label}</div>
            <div className="text-lg font-semibold">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Touches */}
      <section>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <MessageSquare className="w-4 h-4" /> Outreach touches ({data.touches.length})
        </h3>
        {data.touches.length === 0 ? (
          <div className="text-xs text-gray-400">None logged.</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Channel</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Minutes</th>
                  <th className="text-left px-3 py-2">POS</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.touches.map((tc) => (
                  <tr key={tc.id} className="border-t">
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 capitalize">
                        {channelIcon(tc.channel)} {tc.channel}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(tc.date)}</td>
                    <td className="px-3 py-2 text-right">{tc.minutes}</td>
                    <td className="px-3 py-2">{tc.place_of_service || '02'}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-md truncate" title={tc.notes}>
                      {tc.notes || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Progress notes */}
      <section>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4" /> Progress notes ({data.notes.length})
        </h3>
        {data.notes.length === 0 ? (
          <div className="text-xs text-gray-400">None saved.</div>
        ) : (
          <div className="space-y-3">
            {data.notes.map((n) => (
              <div key={n.id} className="rounded-lg border p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-500">
                    {fmtDate(n.date)}
                    {n.author ? ` · ${n.author}` : ''}
                  </div>
                  <Badge className="bg-green-100 text-green-800 capitalize">{n.status || 'saved'}</Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SOAP_FIELDS.map((f) => (
                    <div key={f.key}>
                      <div className="text-xs font-medium text-gray-500">{f.label}</div>
                      <div className="text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                        {n.soap?.[f.key] || <span className="text-gray-400">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Claims */}
      <section>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <Receipt className="w-4 h-4" /> Billing claims ({data.claims.length})
        </h3>
        {data.claims.length === 0 ? (
          <div className="text-xs text-gray-400">No claims generated yet.</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Claim #</th>
                  <th className="text-left px-3 py-2">Payer</th>
                  <th className="text-left px-3 py-2">Service dates</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.claims.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{c.claim_number}</td>
                    <td className="px-3 py-2">{c.payer_name || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.service_from || '—'}
                      {c.service_to && c.service_to !== c.service_from ? ` – ${c.service_to}` : ''}
                    </td>
                    <td className="px-3 py-2 text-right">${c.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2">
                      <Badge className="bg-indigo-100 text-indigo-800 capitalize">{c.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
